import type { ReactNode } from 'react'
import {
  BAND_AVATAR,
  BAND_BAR,
  type CompareStatus,
  initials,
  type ScoreBand,
} from '@/features/entity-resolution/groupFormat'
import { cn } from '@/utils/cn'

/** Square primary avatar for a canonical, or a band-coloured circle for a member. */
export function Avatar({
  name,
  band,
  square = false,
}: {
  name: string
  band?: ScoreBand
  square?: boolean
}) {
  if (square) {
    return (
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md border-2 border-primary/40 bg-primary/10 text-xs font-semibold text-primary">
        {initials(name)}
      </span>
    )
  }
  return (
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-full text-3xs font-semibold ring-2 ring-card',
        band ? BAND_AVATAR[band] : 'bg-muted text-muted-foreground'
      )}
    >
      {initials(name)}
    </span>
  )
}

/** Overlapping stack of member avatars with a "+N" overflow chip. */
export function AvatarStack({
  names,
  bands,
  max = 5,
}: {
  names: string[]
  bands: ScoreBand[]
  max?: number
}) {
  const shown = names.slice(0, max)
  const overflow = names.length - shown.length
  return (
    <div className="flex items-center -space-x-2">
      {shown.map((name, i) => (
        <Avatar key={`${name}-${i}`} name={name} band={bands[i]} />
      ))}
      {overflow > 0 && (
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-3xs font-semibold text-muted-foreground ring-2 ring-card">
          +{overflow}
        </span>
      )}
    </div>
  )
}

/** Segmented score distribution: exact (green) · high (blue) · mid (amber). */
export function ScoreBar({ counts }: { counts: Record<ScoreBand, number> }) {
  const total = counts.exact + counts.high + counts.mid
  if (total === 0) return <div className="h-1.5 rounded-full bg-muted" />
  const segment = (band: ScoreBand) =>
    counts[band] > 0 ? (
      <span className={BAND_BAR[band]} style={{ width: `${(counts[band] / total) * 100}%` }} />
    ) : null
  return (
    <div className="flex h-1.5 overflow-hidden rounded-full bg-muted">
      {segment('exact')}
      {segment('high')}
      {segment('mid')}
    </div>
  )
}

const COMPARATOR: Record<CompareStatus, { sign: string; cls: string }> = {
  approx: { sign: '≈', cls: 'bg-info-surface text-info-foreground' },
  equal: { sign: '=', cls: 'bg-success-surface text-success-foreground' },
  different: { sign: '≠', cls: 'bg-warning-surface text-warning-foreground' },
  missing: { sign: '·', cls: 'bg-muted text-muted-foreground' },
}

/** The middle ≈/=/≠ glyph between two compared values. */
export function ComparatorIcon({ status }: { status: CompareStatus }) {
  const c = COMPARATOR[status]
  return (
    <span
      className={cn(
        'flex size-5 items-center justify-center rounded-full text-xs font-semibold',
        c.cls
      )}
    >
      {c.sign}
    </span>
  )
}

/** Small band-coloured pill (e.g. a score range). */
export function ScorePill({ band, children }: { band: ScoreBand; children: ReactNode }) {
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-3xs font-medium', BAND_AVATAR[band])}>
      {children}
    </span>
  )
}
